import { useEffect, useRef, useState } from 'react'
import type { AxiosError } from 'axios'
import { useQuery, useQueryClient } from 'react-query'
import { userAuth } from '../api/auth'

// import { FacebookAuthProvider, getAuth , GoogleAuthProvider,  OAuthProvider,  signInWithPopup, signOut } from "@firebase/auth";
// import { useAuthState } from 'react-firebase-hooks/auth';
// import firebase from '../api/firebase';

import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { FirebaseAuthentication, Persistence } from '@capacitor-firebase/authentication'

import { isUpdateRequired } from '../functions/versionCompare'
import { initializeEventTypes } from '../types/EventTypes'

const DEFAULT_REMEMBER_ME = true
type ApiErrorResponse = {
  message?: string
  status_code?: number
}

const getStoredRememberMe = () => {
  const storedValue = localStorage.getItem('rememberMe')
  if (storedValue == null) {
    return DEFAULT_REMEMBER_ME
  }

  return storedValue === 'true'
}

const getErrorStatus = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const axiosError = error as AxiosError<ApiErrorResponse>
  return axiosError.response?.data?.status_code || axiosError.response?.status
}

const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return ''
  }
  const axiosError = error as AxiosError<ApiErrorResponse>
  return axiosError.response?.data?.message || ''
}

const useAuthentication = () => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [authBootstrapComplete, setAuthBootstrapComplete] = useState<boolean>(false)
  const [hasCurrentUser, setHasCurrentUser] = useState<boolean | null>(null)
  const [loginError, setLoginError] = useState<boolean>(false)
  const [isAccountDeleted, setIsAccountDeleted] = useState<boolean>(false)
  const [rememberMe, setRememberMe] = useState<boolean>(getStoredRememberMe)
  const [showForceUpdate, setShowForceUpdate] = useState<boolean>(false)
  const queryClient = useQueryClient()
  const isMountedRef = useRef(true)
  // Tracks the currently-authenticated Firebase uid so we can wipe the
  // react-query cache whenever the account changes. Without this, static
  // per-user query keys (e.g. ['getWalletPackages']) leak one account's data
  // to the next account signed in during the same session.
  const lastAuthUidRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false')
  }, [rememberMe])

  const logout = async () => {
    try {
      await FirebaseAuthentication.signOut()
      setUser(null)
      setHasCurrentUser(false)
      setAuthBootstrapComplete(true)
      lastAuthUidRef.current = null
      // Drop ALL cached queries, not just ['auth']. Any user-scoped query with a
      // static key (['getWalletPackages'], profile, bookings, credits, ...) would
      // otherwise survive and be shown to the next account that signs in.
      queryClient.clear()
      setIsLoading(false)
      setShowForceUpdate(false)
    } catch (error) {
      console.error('Sign-Out Error:', error)
      setIsLoading(false)
    }
  }

  const applyPersistence = async (shouldRemember: boolean) => {
    try {
      await FirebaseAuthentication.setPersistence({
        persistence: shouldRemember ? Persistence.BrowserLocal : Persistence.BrowserSession,
      })
      console.log('Persistence Set')
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    let authStateListener: { remove: () => Promise<void> } | null = null
    let idTokenListener: { remove: () => Promise<void> } | null = null

    const bootstrapAuth = async () => {
      const storedRememberMe = getStoredRememberMe()
      setRememberMe(storedRememberMe)
      await applyPersistence(storedRememberMe)

      authStateListener = await FirebaseAuthentication.addListener('authStateChange', ({ user: currentUser }) => {
        if (!isMountedRef.current) return

        setHasCurrentUser(!!currentUser)
        setAuthBootstrapComplete(true)

        // Whenever the authenticated identity actually changes, wipe the cache so
        // no query result from the previous account bleeds into the new one. We
        // skip the very first transition (previousUid === null) so restoring an
        // existing session on app start doesn't needlessly clear.
        const previousUid = lastAuthUidRef.current
        const currentUid = currentUser?.uid ?? null
        if (previousUid !== currentUid) {
          lastAuthUidRef.current = currentUid
          if (previousUid !== null) {
            queryClient.clear()
          }
        }

        if (currentUser) {
          queryClient.invalidateQueries(['auth'])
          return
        }

        setUser(null)
        setShowForceUpdate(false)
        queryClient.removeQueries(['auth'], { exact: true })
      })

      idTokenListener = await FirebaseAuthentication.addListener('idTokenChange', () => {
        if (!isMountedRef.current) return

        setAuthBootstrapComplete(true)
        queryClient.invalidateQueries(['auth'])
      })

      try {
        const currentUser = await FirebaseAuthentication.getCurrentUser()
        const hasUser = !!currentUser?.user

        if (!isMountedRef.current) return

        setHasCurrentUser(hasUser)

        if (hasUser) {
          try {
            await FirebaseAuthentication.getIdToken({ forceRefresh: false })
          } catch (tokenError) {
            console.warn('Failed to restore Firebase token without refresh, retrying.', tokenError)
            try {
              await FirebaseAuthentication.getIdToken({ forceRefresh: true })
            } catch (refreshError) {
              console.warn('Failed to restore Firebase token during bootstrap.', refreshError)
            }
          }
        }
      } catch (error) {
        console.error('Get Current User Error:', error)
        if (isMountedRef.current) {
          setHasCurrentUser(false)
          setUser(null)
          setShowForceUpdate(false)
        }
      } finally {
        if (isMountedRef.current) {
          setAuthBootstrapComplete(true)
          setIsLoading(false)
        }
      }
    }

    bootstrapAuth()

    return () => {
      void authStateListener?.remove()
      void idTokenListener?.remove()
    }
  }, [queryClient])

  useEffect(() => {
    if (!authBootstrapComplete) return

    void applyPersistence(rememberMe)
  }, [authBootstrapComplete, rememberMe])

  const getAccessToken = async () => {
    const result = await FirebaseAuthentication.getIdToken()
    return result.token
  }

  const authQuery = useQuery(['auth'], userAuth, {
    enabled: authBootstrapComplete && hasCurrentUser === true,
    retry: (failureCount, error: unknown) => {
      // Don't retry on auth failures (401/403) — retrying won't help
      const status = getErrorStatus(error)
      if (status === 401 || status === 403) return false
      return failureCount < 3
    },
    refetchOnWindowFocus: false,
    onSuccess: async (res) => {
      setIsLoading(false)
      setUser(res)
      console.log(res)
      console.log('User Authenticated!')
      console.log('User Information: ', res)
      initializeEventTypes()

      // Check for force update requirement
      if (res?.minimum_app_version) {
        try {
          // Only check on mobile platforms (ios/android), skip on web
          const platform = Capacitor.getPlatform()
          if (platform === 'ios' || platform === 'android') {
            const appInfo = await App.getInfo()
            const currentVersion = appInfo.version || '0.0.0'
            const minimumVersion = res.minimum_app_version

            console.log('Version check:', {
              currentVersion,
              minimumVersion,
              platform,
            })

            if (isUpdateRequired(currentVersion, minimumVersion)) {
              console.log('Update required!')
              setShowForceUpdate(true)
            } else {
              setShowForceUpdate(false)
            }
          }
        } catch (error) {
          console.error('Error checking app version:', error)
          // If version check fails, don't block the user
          setShowForceUpdate(false)
        }
      } else {
        // No minimum version specified, allow access
        setShowForceUpdate(false)
      }
    },
    onError: (error: unknown) => {
      console.log('User Not Authenticated!')
      setIsLoading(false)
      const statusCode = getErrorStatus(error)
      const isUnauthorized = statusCode === 401 || statusCode === 403

      if (isUnauthorized) {
        setUser(null)
        setShowForceUpdate(false)
      }

      // Check if the error is due to deleted account
      const errorMessage = getErrorMessage(error)
      if (errorMessage === 'User account has been deleted' && statusCode === 401) {
        setIsAccountDeleted(true)
        // Sign out from Firebase since the account is deleted
        FirebaseAuthentication.signOut().catch(console.error)
      } else {
        setIsAccountDeleted(false)
      }
    },
  })

  const signInWithEmailAndPassword = async (email: string, password: string) => {
    setIsLoading(true)
    setIsAccountDeleted(false) // Reset deleted account state on new login attempt
    setLoginError(false)

    await applyPersistence(rememberMe)

    FirebaseAuthentication.signInWithEmailAndPassword({
      email: email,
      password: password,
      })
      .then(async (result) => {
        setLoginError(false)
        setIsAccountDeleted(false)
        console.log(result)
        await FirebaseAuthentication.getIdToken({ forceRefresh: true })
        queryClient.invalidateQueries(['auth'])
        return result.user
      })
      .catch((error) => {
        setIsLoading(false)
        console.log(error)
        setLoginError(true)
        setIsAccountDeleted(false)
        return null
      })
  }

  const registerWithEmailAndPassword = async (email: string, password: string) => {
    setIsLoading(true)
    await applyPersistence(rememberMe)

    FirebaseAuthentication.createUserWithEmailAndPassword({
      email: email,
      password: password,
    })
      .then(async (result) => {
        console.log(result)
        await FirebaseAuthentication.getIdToken({ forceRefresh: true })
        queryClient.invalidateQueries(['auth'])
        return result.user
      })
      .catch((error) => {
        setIsLoading(false)
        console.log(error)
        return null
      })
  }

  const signInWithGoogle = async () => {
    await applyPersistence(rememberMe)

    try {
      const result = await FirebaseAuthentication.signInWithGoogle()
      console.log('Google Sign-In Result:', result)
      await FirebaseAuthentication.getIdToken({ forceRefresh: true })
      queryClient.invalidateQueries(['auth'])
      return result.user
    } catch (error) {
      console.log('Google Sign-In Error:', error)
      return null
    }
  }
  // const provider = new GoogleAuthProvider()

  // signInWithPopup(auth, provider)
  // .then(() => {

  //     //const credential = GoogleAuthProvider.credentialFromResult(result)
  //     // const token = credential?.accessToken
  //     // const user = result.user

  //     queryClient.invalidateQueries('auth');
  // })
  // .catch(() => {

  //     // const errorCode = error.code
  //     // const errorMessage = error.message
  //     // const email = error.email
  //     // const credential = GoogleAuthProvider.credentialFromError(error)
  // });

  const signInWithFacebook = async () => {
    await applyPersistence(rememberMe)

    try {
      const result = await FirebaseAuthentication.signInWithFacebook()
      console.log('Facebook Sign-In Result:', result)
      await FirebaseAuthentication.getIdToken({ forceRefresh: true })
      queryClient.invalidateQueries(['auth'])
      return result.user
    } catch (error) {
      console.log('Facebook Sign-In Error:', error)
      return null
    }
  }

  const signInWithApple = async () => {
    await applyPersistence(rememberMe)

    try {
      const result = await FirebaseAuthentication.signInWithApple()
      console.log('Apple Sign-In Result:', result)
      await FirebaseAuthentication.getIdToken({ forceRefresh: true })
      queryClient.invalidateQueries(['auth'])
      return result.user
    } catch (error) {
      console.log('Apple Sign-In Error:', error)
      return null
    }
  }

  // const logout = () => {

  //     setIsLoading(true)

  //     signOut(getAuth(firebase))
  //     .then(() => {
  //         setUser(null)
  //         setIsLoading(false)
  //         queryClient.invalidateQueries('auth');
  //     })
  // }

  // useEffect(() => {
  //     if (authUser?.uid !== currentUid) {
  //         queryClient.invalidateQueries('auth');
  //         setCurrentUid(authUser?.uid)
  //     }
  // }, [authUser]);

  const authErrorStatus = getErrorStatus(authQuery.error)
  const hasHardAuthError = authQuery.isError && (authErrorStatus === 401 || authErrorStatus === 403)

  return {
    user: user,
    loading: !authBootstrapComplete || (hasCurrentUser === true && authQuery.isLoading),
    loginLoading: isLoading,
    loginError: loginError,
    isAccountDeleted: isAccountDeleted,
    logout: logout,
    error: hasCurrentUser === true ? hasHardAuthError : false,
    signInWithGoogle: signInWithGoogle,
    signInWithFacebook: signInWithFacebook,
    signInWithApple: signInWithApple,
    signInWithEmailAndPassword: signInWithEmailAndPassword,
    registerWithEmailAndPassword: registerWithEmailAndPassword,
    getAccessToken: getAccessToken,
    rememberMe: rememberMe,
    setRememberMe: setRememberMe,
    showForceUpdate: showForceUpdate,
  }
}

export default useAuthentication
